// Alias-scope resolver: scans a statement and extracts AliasBinding[] for
// every alias declared at the top-level of that statement's FROM / JOIN /
// APPLY / LATERAL / USING / INTO clauses. Sub-query-internal aliases are NOT
// returned here — they belong to inner scopes assembled by resolveScope.
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Scope Resolver — aliases.
//
// Algorithm (single pass, no tree-sitter):
//   1. Walk tokens at paren-depth 0; strings / comments / quoted-ids skipped
//      via the shared tokenSkip helper.
//   2. Structural keywords (FROM / JOIN / CROSS APPLY / OUTER APPLY / LATERAL
//      / USING / INTO / UPDATE target / terminators) flip a state machine;
//      identifiers following a "target" state become QualifiedName bindings;
//      identifiers following an "alias" state commit AliasBinding entries.
//   3. `FROM (` opens a derived-table body — skip to the matching `)` and
//      record the next identifier as an alias whose source is 'derived-table'
//      (or 'lateral'/'cross-apply'/'outer-apply' if the preceding keyword
//      specialized the source).
//
// Comma separators in an old-style FROM list (`FROM a, b, c`) re-enter
// expect='target' with source='from'.

import type { QualifiedName, SqlRange } from '../../sql-grammar/model';
import { buildLineIndex, rangeFromOffsets } from '../../sql-grammar/document/lineIndex';

import type { ParsedBuffer, AliasBinding, AliasSource } from '../types';
import { skipContextFor, trySkipNonCode } from '../classify/tokenSkip';

import {
  readIdentifier,
  readQualifiedName,
  foldIdentifier,
  isBareIdentStart,
  type ReadIdentResult,
} from './identifiers';
import {
  applyKeyword,
  isStructuralKeyword,
  skipParen,
  type ScannerState,
} from './aliasScope.scanner';

interface Pending {
  qualified: QualifiedName;
  qualifiedRange: SqlRange;
  source: AliasSource;
}

export function computeAliasBindings(
  buffer: ParsedBuffer,
  statementId: string,
): AliasBinding[] {
  const stmt = buffer.documentStructure.statements.find((s) => s.id === statementId);
  if (!stmt) return [];
  return computeAliasBindingsForRange(buffer, stmt.range.startOffset, stmt.range.endOffset);
}

// eslint-disable-next-line max-lines-per-function, complexity
export function computeAliasBindingsForRange(
  buffer: ParsedBuffer,
  start: number,
  end: number,
): AliasBinding[] {
  const { parsedDocument } = buffer;
  const { source, options } = parsedDocument;

  const ctx = skipContextFor(options);
  const lineIdx = buildLineIndex(source);
  const out: AliasBinding[] = [];

  const state: ScannerState = {
    expect: 'none', pendingSource: 'from', outerKind: 'unknown', prevKw: null,
  };
  let pending: Pending | null = null;
  let depth = 0;
  let i = start;

  const commitAlias = (ident: ReadIdentResult): void => {
    if (!pending) return;
    out.push({
      alias: ident.value,
      aliasFolded: foldIdentifier(ident.value, ident.quoted, options),
      target: pending.qualified,
      source: pending.source,
      range: rangeFromOffsets(lineIdx, ident.start, ident.end),
    });
    pending = null;
    state.expect = 'none';
    // Note: prevKw is retained so a trailing comma (old-style FROM list) still
    // restarts expect='target' with source='from'.
  };

  while (i < end) {
    // Try to read an identifier (including engine-quoted forms) FIRST — the
    // shared tokenSkip helper would otherwise consume `"…"` / `` `…` `` /
    // `[…]` as if they were strings/comments, which is correct for the
    // clause classifier but wrong here where these are identifiers.
    const leadingIdent = readIdentifier(source, i, end, options);
    if (leadingIdent && (leadingIdent.quoted || isBareIdentStart(source, i, options.engine))) {
      const ident = leadingIdent;
      if (depth > 0) { i = ident.end; continue; }
      const upper = ident.quoted ? null : ident.value.toUpperCase();
      if (upper !== null && isStructuralKeyword(upper)) {
        applyKeyword(state, upper);
        i = ident.end; continue;
      }
      if (state.expect === 'target') {
        const rq = readQualifiedName(source, i, end, options);
        if (rq) {
          pending = { qualified: rq.qualified, qualifiedRange: rq.range, source: state.pendingSource };
          state.expect = 'alias';
          i = rq.range.endOffset; continue;
        }
      }
      if (state.expect === 'alias') {
        commitAlias(ident);
        i = ident.end; continue;
      }
      i = ident.end; continue;
    }

    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }

    const c = source[i];
    if (/\s/.test(c)) { i++; continue; }

    if (c === '(') {
      if (depth === 0 && state.expect === 'target') {
        const closeIdx = skipParen(source, i, end, ctx);
        const src = state.pendingSource;
        const srcKind: AliasSource =
          src === 'lateral' || src === 'cross-apply' || src === 'outer-apply' ? src : 'derived-table';
        pending = {
          qualified: {
            name: '', raw: source.slice(i, closeIdx), quoted: false, foldedName: '',
          },
          qualifiedRange: rangeFromOffsets(lineIdx, i, closeIdx),
          source: srcKind,
        };
        state.expect = 'alias';
        i = closeIdx; continue;
      }
      // SQL/PGQ `GRAPH_TABLE(<graph> MATCH <pattern> COLUMNS (...))` — the
      // function-name read above (state.expect === 'target' branch) already
      // parsed `GRAPH_TABLE` as the pending qualified target, same as any
      // other TVF call. But GRAPH_TABLE is never a real relation — retag it
      // as an opaque `graph-table` derived source (mirrors the FROM-`(`
      // derived-table branch above) so go-to on the alias doesn't chase a
      // phantom `GRAPH_TABLE` relation. Never schema-qualified in real
      // syntax, so only an unqualified name match applies.
      if (
        depth === 0 &&
        state.expect === 'alias' &&
        pending &&
        !pending.qualified.schema &&
        pending.qualified.name.toUpperCase() === 'GRAPH_TABLE'
      ) {
        const closeIdx = skipParen(source, i, end, ctx);
        pending = {
          qualified: {
            name: '', raw: source.slice(pending.qualifiedRange.startOffset, closeIdx), quoted: false, foldedName: '',
          },
          qualifiedRange: rangeFromOffsets(lineIdx, pending.qualifiedRange.startOffset, closeIdx),
          source: 'graph-table',
        };
        i = closeIdx; continue;
      }
      depth++; i++; continue;
    }
    if (c === ')') { if (depth > 0) depth--; i++; continue; }

    if (c === ',' && depth === 0) {
      if (state.prevKw === 'FROM' || state.prevKw === 'JOIN' || state.expect === 'alias' || pending !== null) {
        pending = null;
        state.expect = 'target';
        state.pendingSource = 'from';
      }
      i++; continue;
    }

    // Any other character (operators, numbers, `.`, etc.) is structurally
    // irrelevant for the alias scanner — step over one byte and continue.
    i++;
  }
  return out;
}
